import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import PracticeSession from './PracticeSession';
import UniversalHeader from './UniversalHeader';

// Add consistent styling - MathCampus Teal Design System
const addConsistentStyles = () => {
  if (document.getElementById('student-learning-path-styles')) return;

  const style = document.createElement('style');
  style.id = 'student-learning-path-styles';
  style.textContent = `
    .font-poppins { font-family: 'Poppins', sans-serif; }
    .font-opensans { font-family: 'Open Sans', sans-serif; }
    .text-primary { color: var(--mc-primary, #1CB7C8); }
    .bg-primary { background-color: var(--mc-primary, #1CB7C8); }
    .text-accent { color: var(--mc-primary, #1CB7C8); }
    .bg-accent { background-color: var(--mc-primary, #1CB7C8); }
    .bg-bg-cream { background-color: var(--mc-gray-100, #F5F5F7); }
    .text-text-dark { color: var(--mc-charcoal, #333333); }
    .border-primary { border-color: var(--mc-primary, #1CB7C8); }
    .hover\\:bg-primary:hover { background-color: var(--mc-primary-dark, #159BA9); }
    .hover\\:text-primary:hover { color: var(--mc-primary, #1CB7C8); }
    .hover\\:bg-teal-600:hover { background-color: var(--mc-primary-dark, #159BA9); }
  `;
  document.head.appendChild(style);
};

const StudentLearningPathViewer = ({ selectedTopic, onBack, userProfile }) => {
  const { user } = useAuth();
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStrands, setExpandedStrands] = useState({});
  const [expandedSubtopics, setExpandedSubtopics] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [practiceSessionData, setPracticeSessionData] = useState(null);
  const [showPracticeSession, setShowPracticeSession] = useState(false);
  const [userProgress, setUserProgress] = useState({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const userId = user?.id;
  const userGrade = userProfile?.grade || 'Year 4';

  useEffect(() => {
    addConsistentStyles();
    
    // Add Google Fonts
    if (!document.getElementById('student-learning-path-fonts')) {
      const fontLink = document.createElement('link');
      fontLink.id = 'student-learning-path-fonts';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Open+Sans:wght@400;500;600&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
    
    loadLearningPathPreview();
    loadUserProgress();
  }, [selectedTopic]);

  // Auto-scroll to selected strand when it's expanded
  useEffect(() => {
    if (selectedTopic && selectedTopic.id && previewData && Object.keys(expandedStrands).length > 0) {
      // Small delay to ensure the expansion animation has started
      setTimeout(() => {
        const strandElement = document.getElementById(`strand-${selectedTopic.id}`);
        if (strandElement) {
          strandElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
          console.log('🔍 Auto-scrolled to selected strand:', selectedTopic.strand_name || selectedTopic.name);
        }
      }, 300);
    }
  }, [expandedStrands, previewData, selectedTopic]);

  const loadUserProgress = async () => {
    if (!userId) return;
    
    try {
      console.log('🔍 Loading user progress for user:', userId);
      const response = await fetch(`${backendUrl}/api/learning-path/practice/user-progress/${userId}`);
      if (response.ok) {
        const progressData = await response.json();
        console.log('🔍 Raw progress data received:', progressData);
        
        if (progressData.success) {
          // Check both progress_summaries and completed_sessions
          const summaries = progressData.progress_summaries || [];
          const sessions = progressData.completed_sessions || [];
          
          console.log('🔍 Progress summaries:', summaries);
          console.log('🔍 Completed sessions:', sessions);
          
          // Create a map of completed subtopic+difficulty combinations
          const progressMap = {};
          
          // Process progress summaries if available
          summaries.forEach(progress => {
            const key = `${progress.subtopic_id}-${progress.difficulty_level}`;
            progressMap[key] = {
              completed_sessions: progress.completed_sessions || 0,
              total_sessions: progress.total_sessions || 0,
              average_score: progress.average_score || 0,
              best_score: progress.best_score || 0
            };
          });
          
          // If no summaries but we have sessions, process them directly
          if (summaries.length === 0 && sessions.length > 0) {
            console.log('🔍 No summaries found, processing sessions directly...');
            const sessionMap = {};
            
            sessions.forEach(session => {
              if (session.status === 'completed' && session.subtopic_id && session.difficulty_level) {
                const key = `${session.subtopic_id}-${session.difficulty_level}`;
                if (!sessionMap[key]) {
                  sessionMap[key] = {
                    completed_sessions: 0,
                    total_sessions: 0,
                    scores: []
                  };
                }
                sessionMap[key].completed_sessions++;
                sessionMap[key].total_sessions++;
                if (session.score !== null && session.score !== undefined) {
                  sessionMap[key].scores.push(session.score);
                }
              }
            });
            
            // Convert to progress format
            Object.keys(sessionMap).forEach(key => {
              const data = sessionMap[key];
              const scores = data.scores;
              progressMap[key] = {
                completed_sessions: data.completed_sessions,
                total_sessions: data.total_sessions,
                average_score: scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0,
                best_score: scores.length > 0 ? Math.max(...scores) : 0
              };
            });
            
            console.log('🔍 Progress map from sessions:', progressMap);
          }
          
          setUserProgress(progressMap);
          console.log('✅ Final user progress loaded:', progressMap);
        } else {
          console.log('❌ Progress API returned success: false');
        }
      } else {
        console.log('❌ Progress API response not ok:', response.status);
      }
    } catch (error) {
      console.error('❌ Error loading user progress:', error);
    }
  };

  const loadLearningPathPreview = async () => {
    try {
      setLoading(true);
      
      // First fetch available learning paths for the user's grade
      const learningPathsResponse = await fetch(`${backendUrl}/api/admin/learning-paths`);
      
      if (!learningPathsResponse.ok) {
        console.error('Failed to fetch learning paths');
        return;
      }
      
      const learningPathsData = await learningPathsResponse.json();
      
      if (!learningPathsData.success || !learningPathsData.data.length) {
        console.error('No learning paths available');
        return;
      }
      
      // Find learning path matching user's grade
      const learningPath = learningPathsData.data.find(lp => 
        lp.year_level === userGrade.replace('Year ', '') || 
        lp.year_level === userGrade
      ) || learningPathsData.data[0]; // Fallback to first available
      
      console.log('🔍 Loading student learning path preview for:', learningPath.id, 'Grade:', userGrade);
      
      // Use the same proven API endpoint as admin preview
      const response = await fetch(`${backendUrl}/api/admin/learning-paths/${learningPath.id}/preview?user_id=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPreviewData(data.data);
          console.log('✅ Student learning path data loaded:', data.data);
          
          // Auto-expand the selected strand (from dashboard) or first strand for better UX
          if (data.data.strands && data.data.strands.length > 0) {
            let strandToExpand = data.data.strands[0]; // default to first strand
            
            // If user came from dashboard with a specific strand selected, expand that one
            if (selectedTopic && selectedTopic.id) {
              const matchingStrand = data.data.strands.find(strand => strand.id === selectedTopic.id);
              if (matchingStrand) {
                strandToExpand = matchingStrand;
                console.log('🔍 Auto-expanding selected strand from dashboard:', strandToExpand.strand_name);
              }
            }
            
            // Expand the selected strand
            setExpandedStrands({ [strandToExpand.id]: true });
            
            // Also auto-expand the first subtopic of the selected strand for better UX
            if (strandToExpand.subtopics && strandToExpand.subtopics.length > 0) {
              setExpandedSubtopics({ [strandToExpand.subtopics[0].id]: true });
            }
          }
        } else {
          console.error('Failed to load learning path preview:', data.message);
        }
      } else {
        console.error('Failed to fetch learning path preview:', response.status);
      }
    } catch (error) {
      console.error('Error loading learning path preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStrandExpansion = (strandId) => {
    setExpandedStrands(prev => ({
      ...prev,
      [strandId]: !prev[strandId]
    }));
  };

  const toggleSubtopicExpansion = (subtopicId) => {
    setExpandedSubtopics(prev => ({
      ...prev,
      [subtopicId]: !prev[subtopicId]
    }));
  };

  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const handleStartLesson = async (task, subtopic) => {
    try {
      console.log('🔍 Starting lesson for task:', task, 'in subtopic:', subtopic);
      
      if (!task.questions || task.questions.length === 0) {
        alert('No questions available for this lesson yet.');
        return;
      }

      // Create a real practice session using the backend API
      const sessionCreateResponse = await fetch(`${backendUrl}/api/learning-path/practice/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          learning_path_id: previewData.id,
          subtopic_id: subtopic.id, // Database UUID
          subtopic_identifier: subtopic.subtopic_id, // String identifier like 'number_place_value'
          subtopic_name: subtopic.subtopic_name,
          difficulty_level: task.difficulty,
          duration_minutes: 15,
          questions_count: task.questions.length
        })
      });

      if (sessionCreateResponse.ok) {
        const sessionData = await sessionCreateResponse.json();
        if (sessionData.success) {
          // Use the created session with the task questions
          const practiceSession = {
            success: true,
            session: sessionData.session,
            questions: task.questions.slice(0, 15) // Use admin preview questions
          };

          console.log('✅ Created real practice session:', practiceSession);
          setPracticeSessionData(practiceSession);
          setShowPracticeSession(true);
        } else {
          console.error('Failed to create practice session:', sessionData.message);
          alert('Failed to start lesson. Please try again.');
        }
      } else {
        console.error('Failed to create practice session:', sessionCreateResponse.status);
        // Fallback to preview session if API fails
        const mockSession = {
          success: true,
          session: {
            id: 'preview-' + Date.now(),
            user_id: userId,
            learning_path_id: previewData.id,
            subtopic: subtopic.subtopic_name,
            subtopic_id: subtopic.id, // Use database UUID for consistency
            subtopic_identifier: subtopic.subtopic_id, // String identifier
            difficulty_level: task.difficulty,
            duration_minutes: 15,
            questions_count: task.questions.length
          },
          questions: task.questions.slice(0, 15)
        };

        console.log('⚠️ Using fallback preview session:', mockSession);
        setPracticeSessionData(mockSession);
        setShowPracticeSession(true);
      }
      
    } catch (error) {
      console.error('Error starting lesson:', error);
      alert('Error starting lesson. Please try again.');
    }
  };

  const handlePracticeBack = () => {
    setShowPracticeSession(false);
    setPracticeSessionData(null);
    // Refresh learning path data and user progress to show updated completion status
    loadLearningPathPreview();
    loadUserProgress();
  };

  const handlePracticeComplete = (score) => {
    console.log('🎉 Practice session completed with score:', score);
    // Return to learning path view and refresh data
    handlePracticeBack();
  };

  // Helper function to get progress for a specific task
  const getTaskProgress = (subtopic, task) => {
    // The progress is stored using the database UUID (subtopic.id) as the key
    // Based on the logs, progress keys are in format: {subtopic_uuid}-{difficulty}
    // e.g., "6921c6a4-5d24-4f8d-81b5-c1e0aa49089b-easy"
    
    const primaryKey = `${subtopic.id}-${task.difficulty}`;
    
    if (userProgress[primaryKey]) {
      console.log('🔍 Found progress for UUID key:', primaryKey, userProgress[primaryKey]);
      return userProgress[primaryKey];
    }
    
    // Fallback: try other formats in case there are legacy entries
    const fallbackKeys = [
      `${subtopic.subtopic_id}-${task.difficulty}`, // String identifier  
      `${subtopic.subtopic_name}-${task.difficulty}` // Subtopic name
    ];
    
    for (const key of fallbackKeys) {
      if (userProgress[key]) {
        console.log('🔍 Found progress for fallback key:', key, userProgress[key]);
        return userProgress[key];
      }
    }
    
    console.log('🔍 No progress found for subtopic UUID:', subtopic.id, 'task difficulty:', task.difficulty);
    console.log('🔍 Tried primary key:', primaryKey);
    console.log('🔍 Available progress keys:', Object.keys(userProgress));
    return { completed_sessions: 0, total_sessions: 0, average_score: 0, best_score: 0 };
  };

  // Helper function to determine if a task is completed
  const isTaskCompleted = (subtopic, task) => {
    const progress = getTaskProgress(subtopic, task);
    return progress.completed_sessions > 0;
  };

  // Helper function to get subtopic completion stats
  const getSubtopicStats = (subtopic) => {
    const completedTasks = subtopic.tasks.filter(task => isTaskCompleted(subtopic, task)).length;
    const totalTasks = subtopic.tasks.length;
    const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    return { completedTasks, totalTasks, completionPercentage };
  };

  if (showPracticeSession && practiceSessionData) {
    return (
      <PracticeSession
        sessionData={practiceSessionData}
        onComplete={handlePracticeComplete}
        onBack={handlePracticeBack}
        userProfile={userProfile}
      />
    );
  }

  if (loading) {
    return (
      <div className="font-opensans text-text-dark bg-bg-cream min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading your learning path...</p>
        </div>
      </div>
    );
  }

  if (!previewData || !previewData.strands || previewData.strands.length === 0) {
    return (
      <div className="font-opensans text-text-dark bg-bg-cream min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">📚</div>
          <h2 className="text-2xl font-poppins font-bold text-primary mb-2">No Learning Content Available</h2>
          <p className="text-gray-600 mb-4">This learning path doesn't have any content yet.</p>
          <button
            onClick={onBack}
            className="bg-primary hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium transition-all transform hover:scale-105"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="font-opensans text-text-dark bg-bg-cream min-h-screen">
      <UniversalHeader
        currentPage="learning-journey"
        onBackToDashboard={onBack}
        breadcrumbs={[
          { label: 'Dashboard', action: onBack, current: false },
          { label: 'Learning Paths', action: onBack, current: false },
          { label: selectedTopic?.name || 'Learning Journey', action: null, current: true }
        ]}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Panel */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          <h3 className="text-lg font-poppins font-semibold text-primary mb-4">📊 Your Learning Journey</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg border-2 shadow-md" style={{ background: 'var(--mc-primary-lighter)', borderColor: 'var(--mc-primary-light)' }}>
              <div className="text-2xl font-poppins font-bold" style={{ color: 'var(--mc-primary)' }}>{previewData.statistics.total_questions}</div>
              <div className="text-sm font-semibold" style={{ color: 'var(--mc-gray-600)' }}>Total Questions</div>
            </div>
            <div className="text-center p-4 rounded-lg border-2 shadow-md" style={{ background: 'var(--mc-primary-lighter)', borderColor: 'var(--mc-primary-light)' }}>
              <div className="text-2xl font-poppins font-bold" style={{ color: 'var(--mc-primary)' }}>{previewData.statistics.questions_by_difficulty.easy}</div>
              <div className="text-sm font-semibold" style={{ color: 'var(--mc-gray-600)' }}>Easy Level</div>
            </div>
            <div className="text-center p-4 rounded-lg border-2 shadow-md" style={{ background: 'var(--mc-primary-lighter)', borderColor: 'var(--mc-primary-light)' }}>
              <div className="text-2xl font-poppins font-bold" style={{ color: 'var(--mc-primary)' }}>{previewData.statistics.questions_by_difficulty.medium}</div>
              <div className="text-sm font-semibold" style={{ color: 'var(--mc-gray-600)' }}>Medium Level</div>
            </div>
            <div className="text-center p-4 rounded-lg border-2 shadow-md" style={{ background: 'var(--mc-primary-lighter)', borderColor: 'var(--mc-primary-light)' }}>
              <div className="text-2xl font-poppins font-bold" style={{ color: 'var(--mc-primary)' }}>{previewData.statistics.questions_by_difficulty.hard}</div>
              <div className="text-sm font-semibold" style={{ color: 'var(--mc-gray-600)' }}>Hard Level</div>
            </div>
          </div>
        </div>

        {/* Learning Content */}
        <div className="space-y-6">
          {previewData.strands.map((strand) => (
            <div key={strand.id} id={`strand-${strand.id}`} className={`bg-gray-100 rounded-2xl shadow-lg overflow-hidden border-2 ${
              selectedTopic && selectedTopic.id === strand.id
                ? 'ring-4 shadow-2xl'
                : 'border-gray-400'
            }`} style={selectedTopic && selectedTopic.id === strand.id ? {
              ringColor: 'var(--mc-primary)',
              background: 'var(--mc-primary-lighter)',
              borderColor: 'var(--mc-primary)'
            } : {}}>
              {/* Strand Header */}
              <div className="text-white shadow-lg border-2" style={{
                background: 'var(--mc-primary)',
                borderColor: 'var(--mc-primary-dark)'
              }}>
                <button
                  onClick={() => toggleStrandExpansion(strand.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-black hover:bg-opacity-20 transition-all transform hover:scale-[1.02] text-white font-bold"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">
                      {expandedStrands[strand.id] ? '📂' : '📁'}
                    </span>
                    <div className="text-left">
                      <h4 className="text-xl font-poppins font-bold text-white">
                        {strand.strand_name}
                      </h4>
                      <p className="text-blue-100">
                        {strand.subtopics.length} topics • {strand.subtopics.reduce((sum, sub) => sum + sub.tasks.length, 0)} lessons
                      </p>
                    </div>
                  </div>
                  <span className="text-xl">
                    {expandedStrands[strand.id] ? '🔽' : '▶️'}
                  </span>
                </button>
              </div>

              {/* Strand Content */}
              {expandedStrands[strand.id] && (
                <div className="p-6 space-y-4">
                  {strand.subtopics.map((subtopic) => (
                    <div key={subtopic.id} className="border border-gray-200 rounded-xl overflow-hidden">
                      {/* Subtopic Header */}
                      <div className={`${getSubtopicStats(subtopic).completionPercentage === 100 ? 'bg-green-300 border-green-600' : 'bg-gray-300 border-gray-600'} border-2 shadow-sm`}>
                        <button
                          onClick={() => toggleSubtopicExpansion(subtopic.id)}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-400 transition-colors text-gray-950 font-semibold"
                        >
                          <div className="flex items-center space-x-2">
                            {getSubtopicStats(subtopic).completionPercentage === 100 && (
                              <span className="text-green-900 text-lg">✅</span>
                            )}
                            <span className="text-lg">
                              {expandedSubtopics[subtopic.id] ? '📖' : '📑'}
                            </span>
                            <div className="text-left">
                              <div className="flex items-center space-x-2">
                                <h5 className="font-poppins font-semibold text-primary">
                                  {subtopic.subtopic_name}
                                </h5>
                                {getSubtopicStats(subtopic).completedTasks > 0 && (
                                  <span className="text-xs bg-green-400 text-green-950 px-2 py-1 rounded-full font-bold border border-green-600">
                                    {getSubtopicStats(subtopic).completionPercentage}%
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {subtopic.tasks.length} difficulty levels • {subtopic.tasks.reduce((sum, task) => sum + task.questions.length, 0)} questions
                                {getSubtopicStats(subtopic).completedTasks > 0 && (
                                  <span className="text-green-600 ml-2">
                                    • {getSubtopicStats(subtopic).completedTasks}/{getSubtopicStats(subtopic).totalTasks} completed
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <span className="text-gray-500">
                            {expandedSubtopics[subtopic.id] ? '🔽' : '▶️'}
                          </span>
                        </button>
                      </div>

                      {/* Subtopic Content - Difficulty Levels */}
                      {expandedSubtopics[subtopic.id] && (
                        <div className="p-4 space-y-3">
                          {subtopic.tasks
                            .sort((a, b) => {
                              const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
                              return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
                            })
                            .map((task) => (
                              <div key={task.id} className={`border-2 rounded-lg overflow-hidden shadow-sm ${
                                isTaskCompleted(subtopic, task) ? 'bg-green-300 border-green-600' : 'bg-gray-200 border-gray-500'
                              }`}>
                                <div className="p-4 flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    {isTaskCompleted(subtopic, task) && (
                                      <span className="text-green-900 text-lg">✅</span>
                                    )}
                                    <span className={`px-3 py-1 text-sm font-bold rounded-full border-2 ${
                                      task.difficulty === 'easy' ? 'bg-green-400 text-green-950 border-green-600' :
                                      task.difficulty === 'medium' ? 'bg-amber-400 text-amber-950 border-amber-600' :
                                      'bg-red-400 text-red-950 border-red-600'
                                    }`}>
                                      {task.difficulty.toUpperCase()}
                                    </span>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {task.questions.length} Questions Available
                                      </p>
                                      <p className="text-sm text-gray-600">
                                        Practice {subtopic.subtopic_name} at {task.difficulty} level
                                      </p>
                                      {isTaskCompleted(subtopic, task) && (
                                        <div className="flex items-center space-x-4 mt-1">
                                          <span className="text-xs text-green-600 font-medium">
                                            ✓ Completed {getTaskProgress(subtopic, task).completed_sessions} times
                                          </span>
                                          {getTaskProgress(subtopic, task).best_score > 0 && (
                                            <span className="text-xs text-blue-600 font-medium">
                                              🏆 Best: {getTaskProgress(subtopic, task).best_score}%
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <button
                                    onClick={() => handleStartLesson(task, subtopic)}
                                    className={`px-6 py-2 rounded-lg font-medium transition-all transform hover:scale-105 ${
                                      task.questions.length > 0
                                        ? isTaskCompleted(subtopic, task)
                                          ? 'text-white'
                                          : 'bg-primary text-white'
                                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    }`}
                                    style={task.questions.length > 0 && isTaskCompleted(subtopic, task) ? {
                                      background: 'var(--mc-primary)',
                                      opacity: 0.9
                                    } : task.questions.length > 0 ? {
                                      background: 'var(--mc-primary)'
                                    } : {}}
                                    onMouseEnter={(e) => {
                                      if (task.questions.length > 0) {
                                        e.target.style.background = 'var(--mc-primary-dark)';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (task.questions.length > 0) {
                                        e.target.style.background = 'var(--mc-primary)';
                                      }
                                    }}
                                    disabled={task.questions.length === 0}
                                  >
                                    {task.questions.length === 0 ? '⏳ Coming Soon' :
                                     isTaskCompleted(subtopic, task) ? '🔄 Practice Again' : '🚀 Start Lesson'}
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StudentLearningPathViewer;